import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

/**
 * Embedded interactive map picker for unit creation (Leaflet + OpenStreetMap).
 * Jaime-approved UX: the user pans/zooms and clicks/taps WHERE THE UNIT IS.
 * A click drops a marker, stores latitude/longitude, and reverse-geocodes
 * (Nominatim) to auto-fill the address fields. Auto-filled fields stay
 * editable in the parent form; lat/lng always reflect the last marker.
 * Nominatim calls happen ONLY on marker placement (never on map pan), are
 * deduped per exact coordinate pair, and previous in-flight requests are
 * aborted. Nominatim usage policy: max 1 req/s — one call per marker click
 * is comfortably within that.
 */
export type MapPickedAddress = {
  street?: string;
  number?: string;
  neighborhood?: string;
  locality?: string;
  postal_code?: string;
  country?: string;
  city?: string;
};

type Props = {
  latitude: string;
  longitude: string;
  disabled?: boolean;
  onPick: (latitude: string, longitude: string) => void;
  onAddress: (address: MapPickedAddress) => void;
};

const DEFAULT_CENTER: L.LatLngExpression = [20.6597, -103.3496]; // Guadalajara
const DEFAULT_ZOOM = 12;

// Bundlers break Leaflet's default marker icon URLs; use an inline SVG icon.
const markerIcon = L.divIcon({
  className: 'vecindus-map-marker',
  html: '<svg width="28" height="40" viewBox="0 0 28 40" xmlns="http://www.w3.org/2000/svg"><path d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.3 21.7 0 14 0z" fill="#14b8a6" stroke="#0f766e" stroke-width="1.5"/><circle cx="14" cy="14" r="5.5" fill="#ffffff"/></svg>',
  iconSize: [28, 40],
  iconAnchor: [14, 40],
});

async function reverseGeocode(lat: number, lng: number, signal: AbortSignal): Promise<MapPickedAddress> {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&zoom=18&lat=${lat}&lon=${lng}`;
  const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Nominatim ${res.status}`);
  const data = await res.json();
  const a = data?.address ?? {};
  return {
    street: a.road ?? undefined,
    number: a.house_number ?? undefined,
    neighborhood: a.neighbourhood ?? a.suburb ?? a.quarter ?? a.city_district ?? undefined,
    locality: a.city ?? a.town ?? a.village ?? a.municipality ?? undefined,
    postal_code: a.postcode ?? undefined,
    country: a.country ?? undefined,
    city: a.city ?? a.town ?? a.village ?? undefined,
  };
}

export function MapLocationPicker({ latitude, longitude, disabled, onPick, onAddress }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const accuracyCircleRef = useRef<L.Circle | null>(null);
  const lastQueryRef = useRef<string>('');
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoError, setGeoError] = useState('');

  // Init map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const initialLat = Number(latitude);
    const initialLng = Number(longitude);
    const hasInitial = Number.isFinite(initialLat) && Number.isFinite(initialLng) && (latitude.trim() !== '' || longitude.trim() !== '');
    const map = L.map(containerRef.current, {
      center: hasInitial ? [initialLat, initialLng] : DEFAULT_CENTER,
      zoom: hasInitial ? 17 : DEFAULT_ZOOM,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;

    // FEATURE (2026-09-03, Jaime): preset the map to the user's current
    // location. Skipped when the parent form already carries coordinates
    // (edit flows / form reset). On success we center the map, drop the
    // marker (keeping tap-to-move), and show an accuracy circle. Any
    // denial/failure/unavailability silently falls back to the Guadalajara
    // default — never an unhandled rejection.
    if (!hasInitial && 'geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          // Map may have been unmounted while the permission prompt was open.
          if (!mapRef.current || mapRef.current !== map) return;
          const { latitude: la, longitude: lo, accuracy } = pos.coords;
          map.setView([la, lo], Math.max(DEFAULT_ZOOM, 16));
          onPick(la.toFixed(6), lo.toFixed(6));
          if (Number.isFinite(accuracy) && accuracy > 0) {
            accuracyCircleRef.current = L.circle([la, lo], {
              radius: Math.min(accuracy, 500),
              color: '#14b8a6',
              weight: 1,
              fillOpacity: 0.1,
            }).addTo(map);
          }
        },
        (err) => {
          console.warn('Geolocation unavailable; using Guadalajara default', err?.message ?? err);
        },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
      );
    }

    return () => {
      map.remove(); mapRef.current = null; markerRef.current = null; accuracyCircleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep marker in sync when parent lat/lng changes (e.g. form reset).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const lat = Number(latitude);
    const lng = Number(longitude);
    if (latitude.trim() === '' || longitude.trim() === '' || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { icon: markerIcon, keyboard: false }).addTo(map);
    }
    accuracyCircleRef.current?.remove();
    accuracyCircleRef.current = null;
    map.setView([lat, lng], Math.max(map.getZoom(), 16));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  // Click/tap on the map = marker placement + one reverse-geocode per placement.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onMapClick = (e: L.LeafletMouseEvent) => {
      if (disabled) return;
      const lat = e.latlng.lat;
      const lng = e.latlng.lng;
      onPick(lat.toFixed(6), lng.toFixed(6));
      if (markerRef.current) {
        markerRef.current.setLatLng(e.latlng);
      } else {
        markerRef.current = L.marker(e.latlng, { icon: markerIcon, keyboard: false }).addTo(map);
      }
      // Dedupe: skip identical coordinate pairs already geocoded.
      const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
      if (lastQueryRef.current === key) return;
      lastQueryRef.current = key;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setGeoBusy(true);
      setGeoError('');
      reverseGeocode(lat, lng, controller.signal)
        .then((address) => onAddress(address))
        .catch((err) => {
          if (controller.signal.aborted) return;
          lastQueryRef.current = '';
          setGeoError('No pudimos autocompletar la dirección desde el mapa. Puedes escribirla manualmente.');
          console.warn('Nominatim reverse geocode failed', err);
        })
        .finally(() => { if (!controller.signal.aborted) setGeoBusy(false); });
    };
    map.on('click', onMapClick);
    return () => { map.off('click', onMapClick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, onPick, onAddress]);

  return (
    <div className="space-y-2">
      <p className="text-xs text-[var(--v-text-secondary)]">
        {geoBusy ? 'Autocompletando dirección desde el mapa…' : 'Toca el mapa en la ubicación de la propiedad'}
      </p>
      <div
        ref={containerRef}
        className={disabled ? 'h-64 w-full rounded-xl border border-[var(--v-border-light)] opacity-60' : 'h-64 w-full rounded-xl border border-[var(--v-border-light)]'}
        style={{ zIndex: 0 }}
      />
      {geoError && <p role="alert" className="text-xs text-[var(--v-danger)]">{geoError}</p>}
      {latitude.trim() !== '' && longitude.trim() !== '' && (
        <p className="text-xs text-[var(--v-text-tertiary)]">Ubicación marcada: {latitude}, {longitude}</p>
      )}
    </div>
  );
}
